import ChamadosAPI from 'dashboard/api/studio/chamados';

export const types = {
  SET_UI_FLAG: 'SET_CHAMADOS_UI_FLAG',
  SET_RECORDS: 'SET_CHAMADOS_RECORDS',
  SET_META: 'SET_CHAMADOS_META',
  SET_CURRENT: 'SET_CHAMADOS_CURRENT',
  SET_OPEN_ID: 'SET_CHAMADOS_OPEN_ID',
  SET_FILTERS: 'SET_CHAMADOS_FILTERS',
  SET_NEWS: 'SET_CHAMADOS_NEWS',
  SET_NEWS_ENABLED: 'SET_CHAMADOS_NEWS_ENABLED',
  UPSERT: 'UPSERT_CHAMADO',
  REMOVE: 'REMOVE_CHAMADO',
};

const initialFilters = () => ({
  ticketType: null,
  status: null,
  severity: null,
  search: null,
  reporterId: null,
  page: 1,
  perPage: 25,
});

const state = {
  records: [],
  meta: { totalCount: 0, page: 1, perPage: 25, totalPages: 0 },
  current: null,
  // Chamado com o card aberto na tela agora (null = nenhum). É ele que decide
  // "quem está olhando já viu" no realtime — o `current` guarda o conteúdo e
  // ficava preso no último aberto depois de fechar o card.
  openId: null,
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    approvingItem: false,
    commentingItem: false,
  },
  filters: initialFilters(),
  news: { count: 0, tone: 'warning' },
  // Ligado pela sidebar só pra quem vê Chamados: o canal é da conta inteira e
  // sem a permissão o endpoint responde 403.
  newsEnabled: false,
};

const getters = {
  getRecords: $state => $state.records,
  getMeta: $state => $state.meta,
  getCurrent: $state => $state.current,
  getUIFlags: $state => $state.uiFlags,
  getFilters: $state => $state.filters,
  // Formato do badge com número da sidebar (getterKeys.count).
  getNewsBadge: $state => $state.news,
};

const buildParams = filters => {
  const params = {};
  if (filters.ticketType) params.ticket_type = filters.ticketType;
  if (filters.status) params.status = filters.status;
  if (filters.severity) params.severity = filters.severity;
  if (filters.search) params.search = filters.search;
  if (filters.reporterId) params.reporter_id = filters.reporterId;
  if (filters.page) params.page = filters.page;
  if (filters.perPage) params.per_page = filters.perPage;
  return params;
};

const actions = {
  // silent: rebusca de fundo (useChamadosSync) não troca a tabela pelo
  // "carregando" a cada minuto.
  async fetchList({ commit, state: $state }, { silent = false } = {}) {
    if (!silent) commit(types.SET_UI_FLAG, { fetchingList: true });
    try {
      const { data } = await ChamadosAPI.list(buildParams($state.filters));
      commit(types.SET_RECORDS, data.data || []);
      commit(types.SET_META, data.meta || {});
    } finally {
      if (!silent) commit(types.SET_UI_FLAG, { fetchingList: false });
    }
  },

  async fetchOne({ commit, dispatch, state: $state }, id) {
    commit(types.SET_OPEN_ID, id);
    commit(types.SET_UI_FLAG, { fetchingItem: true });
    try {
      const { data } = await ChamadosAPI.show(id);
      // Abrir marca como visto no servidor: some o negrito da linha e o número,
      // mesmo que o card já tenha fechado antes da resposta.
      if ($state.records.some(r => r.id === data.id)) {
        commit(types.UPSERT, { id: data.id, has_news: false });
      }
      dispatch('refreshNews');
      // Card fechado (ou outro aberto) antes da resposta: não ressuscita.
      if ($state.openId === id) commit(types.SET_CURRENT, data);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { fetchingItem: false });
    }
  },

  async create({ commit }, formData) {
    commit(types.SET_UI_FLAG, { creatingItem: true });
    try {
      const { data } = await ChamadosAPI.create(formData);
      commit(types.UPSERT, data);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { creatingItem: false });
    }
  },

  // Resposta do cliente no fio (§3.5). Append otimista com dedup por id —
  // o broadcast comment.added da própria resposta chega depois e é ignorado.
  async addComment({ commit, state: $state }, { id, body, files = [] }) {
    commit(types.SET_UI_FLAG, { commentingItem: true });
    try {
      const { data } = await ChamadosAPI.comment(id, { body, files });
      const current = $state.current;
      if (
        current?.id === id &&
        !(current.comments || []).some(c => c.id === data.id)
      ) {
        commit(types.SET_CURRENT, {
          ...current,
          comments: [...(current.comments || []), data],
        });
      }
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { commentingItem: false });
    }
  },

  async approve({ commit, state: $state }, { id, approvalNote }) {
    commit(types.SET_UI_FLAG, { approvingItem: true });
    try {
      const { data } = await ChamadosAPI.approve(id, { approvalNote });
      commit(types.UPSERT, data);
      if ($state.current?.id === data.id) commit(types.SET_CURRENT, data);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { approvingItem: false });
    }
  },

  async reject({ commit, state: $state }, { id, approvalNote }) {
    commit(types.SET_UI_FLAG, { approvingItem: true });
    try {
      const { data } = await ChamadosAPI.reject(id, { approvalNote });
      commit(types.UPSERT, data);
      if ($state.current?.id === data.id) commit(types.SET_CURRENT, data);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { approvingItem: false });
    }
  },

  async enableNews({ commit, dispatch }) {
    commit(types.SET_NEWS_ENABLED, true);
    await dispatch('fetchNews');
  },

  async fetchNews({ commit }) {
    const { data } = await ChamadosAPI.news();
    commit(types.SET_NEWS, data);
  },

  // Evento de chamado no cable: atualiza o número de quem tem o badge.
  async refreshNews({ state: $state, dispatch }) {
    if ($state.newsEnabled) await dispatch('fetchNews');
  },

  setFilters({ commit }, partial) {
    commit(types.SET_FILTERS, partial);
  },

  resetFilters({ commit }) {
    commit(types.SET_FILTERS, initialFilters());
  },

  closeCurrent({ commit }) {
    commit(types.SET_OPEN_ID, null);
    commit(types.SET_CURRENT, null);
  },

  // Recebe chamado.event do ActionCable (SupportTickets::Broadcaster).
  // Updates de ticket: { event_type, ticket: { id, ... } }
  // Comentário público: { event_type: 'comment.added', ticket_id, comment: {...} }

  handleRealtimeEvent({ commit, state: $state }, payload) {
    if (!payload?.event_type) return;

    if (payload.event_type === 'comment.added') {
      const current = $state.current;
      const comment = payload.comment;
      if (!comment) return;
      if (
        $state.openId !== payload.ticket_id ||
        current?.id !== payload.ticket_id
      ) {
        // Resposta do agente do Gestão num chamado fechado na tela: novidade.
        const row = $state.records.find(r => r.id === payload.ticket_id);
        if (
          row &&
          row.status !== 'closed' &&
          comment.origin === 'agent_mirror'
        ) {
          commit(types.REMOVE, payload.ticket_id); // sobe pro topo, como o servidor ordena
          commit(types.UPSERT, { ...row, has_news: true });
        }
        return;
      }
      const comments = current.comments || [];
      if (comments.some(c => c.id === comment.id)) return; // dedup
      commit(types.SET_CURRENT, {
        ...current,
        comments: [...comments, comment],
      });
      return;
    }

    if (!payload?.ticket?.id) return;
    const incoming = payload.ticket;
    const existing = $state.records.find(r => r.id === incoming.id);
    // ticket.* só vem do Gestão: novidade, a menos que esteja aberto na tela
    // ou encerrado (mesmo corte do SupportTicket::NEWS_SQL).
    const hasNews =
      $state.openId !== incoming.id && incoming.status !== 'closed';
    if (existing) {
      if (hasNews) commit(types.REMOVE, incoming.id); // sobe pro topo, como o servidor ordena
      commit(types.UPSERT, { ...existing, ...incoming, has_news: hasNews });
    }
    if ($state.openId === incoming.id && $state.current?.id === incoming.id) {
      commit(types.SET_CURRENT, { ...$state.current, ...incoming });
    }
  },
};

const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_RECORDS]($state, records) {
    $state.records = records;
  },
  [types.SET_META]($state, meta) {
    $state.meta = {
      totalCount: Number(meta.total_count || 0),
      page: Number(meta.page || 1),
      perPage: Number(meta.per_page || 25),
      totalPages: Number(meta.total_pages || 0),
    };
  },
  [types.SET_CURRENT]($state, current) {
    $state.current = current;
  },
  [types.SET_OPEN_ID]($state, id) {
    $state.openId = id;
  },
  [types.SET_NEWS_ENABLED]($state, enabled) {
    $state.newsEnabled = enabled;
  },
  [types.SET_NEWS]($state, news) {
    $state.news = {
      count: Number(news?.count || 0),
      tone: news?.tone || 'warning',
    };
  },
  [types.SET_FILTERS]($state, partial) {
    $state.filters = { ...$state.filters, ...partial };
  },
  [types.UPSERT]($state, ticket) {
    const idx = $state.records.findIndex(r => r.id === ticket.id);
    if (idx >= 0) {
      $state.records = $state.records.map((r, i) =>
        i === idx ? { ...r, ...ticket } : r
      );
    } else {
      $state.records = [ticket, ...$state.records];
    }
  },
  [types.REMOVE]($state, id) {
    $state.records = $state.records.filter(r => r.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
